// What's new (/whats-new; docs/factory.md "Screenshots and What's new"): what
// the merged changes did, in their own few sentences, with the screens before
// and after: the agents' (AgentRun mirrors) and those opened from a laptop
// (a ```whatsnew block in the PullRequest mirror's body, "PRs from a laptop").
// On a phone each feature is a full-height card and the
// cards swipe sideways (scroll-snap, a thin line for where you are); on a
// desktop they are a column with before and after side by side. Arrow keys
// move between features. Live or "Coming in the next update" compares the merge with
// prod's latest release, which needs the network: without it there is no badge.
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { useAllMirrors, useAllRuns } from '@/db/hooks'
import type { PullRequestResource } from '@/kinds/git/apply'
import { useBuilds } from '@/components/ShipPipeline'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { ShotFlip, ShotSideBySide } from '@/components/Shots'
import { Empty, PageTitle, Tag, cx } from '@/components/ui'
import { featuresOf, lastRelease, liveness, type Feature } from '@/lib/whatsNew'

const WIDE = '(min-width: 1024px)'

const mergedLabel = (iso: string) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/** Whether a key press belongs to a field rather than the page. */
const typing = (e: KeyboardEvent) => {
  const t = e.target as HTMLElement | null
  return !!t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))
}

/** Arrow keys step through the features (←/→), as a swipe does. */
const useArrows = (step: (by: 1 | -1) => void) => {
  const latest = useRef(step)
  useEffect(() => {
    latest.current = step
  })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || typing(e)) return
      if (document.querySelector('[role="dialog"]')) return
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault()
        latest.current(e.key === 'ArrowRight' ? 1 : -1)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
}

const Badge = ({ feature, released }: { feature: Feature; released: string | null }) => {
  const state = liveness(feature.merged, released)
  if (!state) return null
  return state === 'live' ? <Tag tone="done">Live</Tag> : <Tag tone="accent">Coming in the next update</Tag>
}

const Meta = ({ feature, released }: { feature: Feature; released: string | null }) => (
  <div className="flex flex-wrap items-center gap-2 text-xs text-faint mb-1.5 min-h-5">
    <Badge feature={feature} released={released} />
    <span>Merged {mergedLabel(feature.merged)}</span>
    <Link
      to="/dev/review/$id"
      params={{ id: feature.id }}
      className="ml-auto text-muted hover:text-fg"
      data-testid="whats-new-change"
    >
      See the change
    </Link>
  </div>
)

const NotesLink = () => (
  <Link
    to="/settings/$section"
    params={{ section: 'about' }}
    className="text-[13px] text-muted hover:text-fg"
  >
    Release notes
  </Link>
)

// ---- phone: the swipe cards ---------------------------------------------------

const Card = ({ feature, released }: { feature: Feature; released: string | null }) => {
  // one phone screen fills what the card has left; a wide one sits at its natural size
  const one = feature.shots.length === 1 && feature.shots[0]!.viewport === 'phone'
  return (
    <article
      className="card h-full flex flex-col px-4 pt-3.5 pb-4 overflow-y-auto overscroll-contain scrollbar-none"
      data-testid="whats-new-card"
      data-id={feature.id}
    >
      <Meta feature={feature} released={released} />
      <h2 className="t-title2 text-balance">{feature.title}</h2>
      {feature.explainer && (
        <p className="text-[15px] leading-relaxed text-fg-2 mt-1.5 whitespace-pre-line">
          {feature.explainer}
        </p>
      )}
      {feature.shots.length > 0 && (
        <div className={cx('mt-4 flex flex-col gap-6', one && 'flex-1 min-h-[16rem]')}>
          {feature.shots.map((s) => (
            <ShotFlip
              key={s.name}
              shot={s}
              className={one ? 'flex-1' : undefined}
              frame={
                one
                  ? 'flex-1 min-h-0 w-full'
                  : s.viewport === 'desktop'
                    ? 'w-full aspect-[1440/900]'
                    : 'w-full h-[52vh]'
              }
            />
          ))}
        </div>
      )}
    </article>
  )
}

const Deck = ({ features, released }: { features: Feature[]; released: string | null }) => {
  const scroller = useRef<HTMLDivElement>(null)
  const thumb = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(0)
  const n = features.length
  const onScroll = () => {
    const el = scroller.current
    if (!el) return
    const at = el.scrollLeft / Math.max(1, el.clientWidth)
    // the line follows the finger: a style write, not a render per frame
    if (thumb.current) thumb.current.style.transform = `translateX(${at * 100}%)`
    setIndex(Math.min(n - 1, Math.max(0, Math.round(at))))
  }
  const go = (i: number) => {
    const el = scroller.current
    if (!el) return
    el.scrollTo({ left: Math.min(n - 1, Math.max(0, i)) * el.clientWidth, behavior: 'smooth' })
  }
  useArrows((by) => go(index + by))
  return (
    <div
      // exactly the screen above the tab bar (and the player), as Home is
      className="-mx-3 -mt-2 flex flex-col h-[calc(100dvh-var(--above-tabbar)-0.75rem-env(safe-area-inset-top))]"
      data-testid="whats-new-deck"
    >
      <header className="px-4 pt-2 pb-3 shrink-0">
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="t-title">What&rsquo;s new</h1>
          <NotesLink />
        </div>
        {n > 1 && (
          <div className="mt-2.5 h-[3px] rounded-full bg-fg/10 overflow-hidden" aria-hidden>
            <div
              ref={thumb}
              className="h-full rounded-full bg-fg/70"
              style={{ width: `${100 / n}%` }}
              data-testid="whats-new-progress"
            />
          </div>
        )}
      </header>
      <div
        ref={scroller}
        onScroll={onScroll}
        className="flex-1 min-h-0 flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory overscroll-x-contain scrollbar-none"
        role="region"
        aria-roledescription="carousel"
        aria-label="What's new"
        data-testid="whats-new-scroller"
        data-index={index}
      >
        {features.map((f) => (
          <section
            key={f.id}
            className="w-full shrink-0 snap-center snap-always px-3 pb-1 h-full"
            aria-roledescription="slide"
            aria-label={f.title}
          >
            <Card feature={f} released={released} />
          </section>
        ))}
      </div>
    </div>
  )
}

// ---- desktop: a column, before and after side by side -----------------------------

const Column = ({ features, released }: { features: Feature[]; released: string | null }) => {
  const cards = useRef<(HTMLElement | null)[]>([])
  useArrows((by) => {
    // the card nearest the top of the window is the current one
    const tops = cards.current.map((c) => (c ? Math.abs(c.getBoundingClientRect().top - 16) : Infinity))
    const now = tops.indexOf(Math.min(...tops))
    const next = cards.current[Math.min(features.length - 1, Math.max(0, now + by))]
    next?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
  return (
    <div className="max-w-5xl pb-16" data-testid="whats-new-column">
      <PageTitle right={<NotesLink />}>What&rsquo;s new</PageTitle>
      <div className="flex flex-col gap-6">
        {features.map((f, i) => (
          <article
            key={f.id}
            ref={(el) => {
              cards.current[i] = el
            }}
            className="card px-6 py-5 scroll-mt-4"
            data-testid="whats-new-card"
            data-id={f.id}
          >
            <Meta feature={f} released={released} />
            <h2 className="t-title2">{f.title}</h2>
            {f.explainer && (
              <p className="text-[15px] leading-relaxed text-fg-2 mt-1.5 max-w-[68ch] whitespace-pre-line">
                {f.explainer}
              </p>
            )}
            {f.shots.length > 0 && (
              <div className="mt-5 flex flex-col gap-6">
                {f.shots.map((s) => (
                  <ShotSideBySide key={s.name} shot={s} />
                ))}
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  )
}

/** Prod's latest release, read only while the page shows features. */
const WithReleases = ({ children }: { children: (released: string | null) => ReactNode }) => {
  const { data } = useBuilds()
  return <>{children(lastRelease(data?.releases))}</>
}

export const WhatsNewView = () => {
  const runs = useAllRuns()
  const pulls = useAllMirrors('PullRequest') as PullRequestResource[] | undefined
  const wide = useMediaQuery(WIDE)
  // both, or the PRs from a laptop would pop in after the agents' on a first visit
  if (runs === undefined || pulls === undefined) return null
  const features = featuresOf(runs, pulls)
  if (!features.length)
    return (
      <div>
        <PageTitle right={<NotesLink />}>What&rsquo;s new</PageTitle>
        <Empty icon="✦">
          When a change merges with a few words on what it does, or with screenshots, it shows here.
        </Empty>
      </div>
    )
  return (
    <WithReleases>
      {(released) =>
        wide ? (
          <Column features={features} released={released} />
        ) : (
          <Deck features={features} released={released} />
        )
      }
    </WithReleases>
  )
}
